import { useEffect, useMemo, useState } from "react";
import { bool, func, object, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useEstimateStructure, PercentSettingContents } from "@tools";
import { WORK_TYPE_SETTINGS } from "@tools/EstimateEditor/utils";
import { Accordion, Alert, Button } from "@components";
import Grid from "@material-ui/core/Grid";
import { Percent } from "@assets";

export const AutomationSettings = observer(({
  workType,
  expandedAccordion,
  onToggle,
  advancedSettingsExpanded,
  onAdvancedSettingsToggle,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const [ localPercent, setPercent ] = useState(1);
  const [ placeholder, setPlaceholder ] = useState(undefined);
  const [ hasDifferentSettingsInSections, setHasDifferentSettingsInSections ] = useState(undefined);
  const [ usedWorkTypes, setUsedWorkTypes ] = useState([]);
  const [ usedSections, setUsedSections ] = useState([]);
  const [ differentSectionAutomationAlertVisible, showDifferentSectionAutomationAlert ] = useState(false);
  
  const { id, name } = workType;
  
  const expanded = useMemo(() => (
    expandedAccordion === WORK_TYPE_SETTINGS.AUTOMATION
  ), [ expandedAccordion ]);
  
  const disabled = useMemo(() => (
    !usedWorkTypes.length || typeof localPercent !== "number" || usedSections.length === 0
  ), [ usedWorkTypes, localPercent, usedSections ]);
  
  useEffect(() => {
    if(!expanded)
      return;
    
    resetSettings();
  }, [ expanded ]);
  
  // const availableWorkTypes = useMemo(() => (
  //   structure.availableAutomationWorkTypes(id)
  // ), [structure.workTypes]);
  
  const blacklistedWorkTypes = structure.blacklistedAutomationWorkTypesPerSection(id)
  
  const resetSettings = () => {
    const currentAutomation = structure.getGlobalWorkTypeAutomationSettings(id);
    
    setPercent(currentAutomation.percent);
    setUsedWorkTypes(currentAutomation.of);
    setPlaceholder(currentAutomation.placeholder);
    setUsedSections(currentAutomation.sections);
    setHasDifferentSettingsInSections(currentAutomation.differentPercents);
  }

  const handleToggle = () => onToggle(WORK_TYPE_SETTINGS.AUTOMATION, true);
  const handleClose = () => onToggle(WORK_TYPE_SETTINGS.AUTOMATION);

  const handleReset = () => {
    structure.setWorkTypeAutomation(workType.id, 0);
    handleClose();
  };

  // const handleSelectWorkType = (id) => {
  //   setUsedWorkTypes((state) =>
  //     state.includes(id) ? state.filter((s) => s !== id) : [...state, id]
  //   );
  // };
  //
  // const handleSelectSection = (id) => {
  //   setUsedSections((state) =>
  //     state.includes(id) ? state.filter((s) => s !== id) : [...state, id]
  //   );
  // };
  //
  // const handleSelectAllSections = () => {
  //   setUsedSections(
  //     !structure.sections?.length || usedSections.length === structure.sections?.length
  //       ? []
  //       : [...structure.sections.map(s => s.id)]
  //   );
  // };

  const checkSaveEstimationSettings = () => {
    if(hasDifferentSettingsInSections)
      showDifferentSectionAutomationAlert(true);
    else
      saveEstimationSettings();
  }

  const saveEstimationSettings = () => {
    handleClose();
    structure.setWorkTypeAutomation(workType.id, localPercent, usedWorkTypes ? [ ...usedWorkTypes ] : [], usedSections);
    showDifferentSectionAutomationAlert(false);
  };

  return (
    <Accordion
      id={WORK_TYPE_SETTINGS.AUTOMATION}
      title={<>
        <Percent color="primary" className="text-lg" />
        <h3 className="ml-2">
          {t("views.editor.dialogs.work_type_settings.automation")}
        </h3>
      </>}
      expanded={expanded}
      onExpand={handleToggle}
      titleColored
      allowOverflow
    >
      <Grid item container direction="column" className="p-4 gap-3">
        <PercentSettingContents
          percent={localPercent}
          percentPlaceholder={placeholder}
          usedWorkTypes={usedWorkTypes}
          usedSections={usedSections}
          // availableWorkTypes={availableWorkTypes}
          availableWorkTypes={structure.workTypes.filter(wT => wT.id !== workType.id)}
          blacklistedWorkTypes={blacklistedWorkTypes}
          availableSections={structure.sections}
          onPercentChange={setPercent}
          onSelectWorkType={setUsedWorkTypes}
          onSelectSection={setUsedSections}
          editedWorkTypeName={name}
          allowSelectAllSections
          hideSectionList
          maxSectionsVisible={0}
          showSectionsSettings={advancedSettingsExpanded}
          onAdvancedSettingsToggle={onAdvancedSettingsToggle}
          showPercentageWorkTypesTooltip
          allowSelectAllWorkTypes
        />
        <Grid item container justifyContent="flex-end" className="mt-4 gap-2">
          <Button
            variant="outlined"
            className="w-max"
            onClick={handleClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="outlined"
            className="w-max"
            onClick={handleReset}
          >
            {t("views.editor.dialogs.automation.reset")}
          </Button>
          <Button
            variant="contained"
            className="w-max"
            onClick={checkSaveEstimationSettings}
            disabled={disabled}
          >
            {t("common.apply")}
          </Button>
        </Grid>
        <Alert
          title={t("views.editor.dialogs.automation.different_section_autmation_alert.title")}
          isOpen={differentSectionAutomationAlertVisible}
          cancelText={t("common.cancel_full")}
          acceptText={t("views.editor.dialogs.automation.different_section_autmation_alert.accept")}
          onCancel={() => showDifferentSectionAutomationAlert(false)}
          onAccept={saveEstimationSettings}
          width={480}
        >
          <p>{t("views.editor.dialogs.automation.different_section_autmation_alert.content1", { name })}</p>
          <br />
          <p>{t("views.editor.dialogs.automation.different_section_autmation_alert.content2")}</p>
        </Alert>
      </Grid>
    </Accordion>
  );
})

AutomationSettings.propTypes = {
  workType: object.isRequired,
  onToggle: func.isRequired,
  expandedAccordion: string,
  advancedSettingsExpanded: bool,
  onAdvancedSettingsToggle: func,
};
